import React, { useEffect, useState } from "react";
import { Card, Button, Container } from "react-bootstrap";
import {
  FaUsers,
  FaBolt,
  FaCalendarWeek,
  FaCalendarAlt,
  FaCheckCircle,
  FaChartBar,
  FaQuestionCircle,
  FaCopy,
  FaCheck,
} from "react-icons/fa";
import {
  getAthletePortalEntriesTodayCount,
  getAthletePortalEntriesWeekCount,
  getLatestPortalActivityLogs,
} from "../../../../backend/firebaseFunctions";
import FeedbackTemplatesModal from "./FeedbackTemplatesModal";
import CustomModal from "../../../Global/Modal/CustomModal";
import Joyride, { STATUS } from "react-joyride";
import { useAuth } from "../../../../context/AuthContext";

const gridStyles = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "24px",
  margin: "0 auto 32px auto",
  maxWidth: 1200,
  direction: "rtl",
};

const responsiveGridStyles = `
@media (max-width: 991px) {
  .custom-stats-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .custom-stats-grid { grid-template-columns: 1fr; }
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
`;

const statCardStyle = (borderColor) => ({
  borderRadius: 18,
  background: "#fff",
  minHeight: 170,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderTop: `6px solid ${borderColor}`,
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  transition: "box-shadow 0.2s, transform 0.2s",
});

const statNumberStyle = {
  fontWeight: 900,
  fontSize: "2.5rem",
  color: "#222",
  margin: "16px 0 8px 0",
  letterSpacing: 1,
  lineHeight: 1.1,
};

const statLabelStyle = {
  color: "#555",
  fontSize: "1.15rem",
  fontWeight: 600,
  letterSpacing: 0.5,
  marginBottom: 0,
};

const CommunityPane = ({
  students = [],
  groups = [],
  events = [],
  evaluationTemplates,
  setEvaluationTemplates,
  activateAlert,
}) => {
  const { currentUser } = useAuth();
  const [athletePortalTodayCount, setAthletePortalTodayCount] = useState(null);
  const [athletePortalWeekCount, setAthletePortalWeekCount] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);
  const [showFeedbackTemplatesModal, setShowFeedbackTemplatesModal] =
    useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [copyButtonState, setCopyButtonState] = useState({
    isCopied: false,
    icon: <FaCopy />,
  });
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Joyride steps for Community pane
  const tourSteps = [
    {
      target: ".copy-portal-link-btn",
      content: "כאן תוכל להעתיק את קישור הפורטל ולשלוח לספורטאים שלך!",
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: ".custom-stats-grid",
      content:
        "כאן תוכל לראות נתונים סטטיסטיים על פעילות הארגון בפורטל, כניסות לפורטל ואירועים קרובים.",
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: ".centered-feedback-btn",
      content: "כאן תוכל לנהל את תבניות משוב לאירועים.",
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: ".activity-feed-card",
      content:
        "כאן תוכל לצפות בפעילות האחרונה בפורטל - התחברויות, משובים שהוזנו, עדכוני פרטים ועוד.",
      placement: "left",
      disableBeacon: true,
    },
  ];

  // Joyride callback to stop tour
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  // Fetch counts on mount or when currentUser changes
  useEffect(() => {
    if (!currentUser?.uid) return;

    let isMounted = true;
    setLoading(true);
    setError(null);
    Promise.all([
      getAthletePortalEntriesTodayCount(currentUser.uid),
      getAthletePortalEntriesWeekCount(currentUser.uid),
    ])
      .then(([today, week]) => {
        if (isMounted) {
          setAthletePortalTodayCount(today);
          setAthletePortalWeekCount(week);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError("שגיאה בטעינת נתוני פעילות פורטל");
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid]);

  // Fetch latest portal activity logs
  useEffect(() => {
    if (!currentUser?.uid) return;

    let isMounted = true;
    setActivityLoading(true);
    setActivityError(null);
    getLatestPortalActivityLogs(currentUser.uid)
      .then((logs) => {
        if (isMounted) {
          setActivityLogs(logs);
          setActivityLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setActivityError("שגיאה בטעינת פעילות אחרונה");
          setActivityLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid]);

  const numAthletes = students.filter(
    (student) =>
      student?.groupsIDs?.length > 0 &&
      groups.map((group) => group.id).includes(student.groupsIDs[0])
  ).length;

  // Calculate number of events in the next 30 days
  const now = new Date();
  const thirtyDaysFromNow = new Date(now);
  thirtyDaysFromNow.setDate(now.getDate() + 30);
  const numEventsNext30Days = Array.isArray(events)
    ? events.filter((event) => {
      if (!event.date || !event.date.seconds) return false;
      // Convert Firestore timestamp to Date object
      const eventDate = new Date(event.date.seconds * 1000);
      return eventDate >= now && eventDate <= thirtyDaysFromNow;
    }).length
    : 0;

  const stats = [
    {
      icon: <FaUsers size={40} style={{ color: "#1976d2" }} />,
      value: numAthletes,
      label: "ספורטאים בארגון",
      borderColor: "#1976d2",
    },
    {
      icon: <FaBolt size={40} style={{ color: "#43a047" }} />,
      value: loading ? "..." : error ? "-" : athletePortalTodayCount,
      label: "כניסות לפורטל היום",
      borderColor: "#43a047",
    },
    {
      icon: <FaCalendarWeek size={40} style={{ color: "#ff9800" }} />,
      value: loading ? "..." : error ? "-" : athletePortalWeekCount,
      label: "כניסות לפורטל השבוע",
      borderColor: "#ff9800",
    },
    {
      icon: <FaCalendarAlt size={40} style={{ color: "#ab47bc" }} />,
      value: numEventsNext30Days,
      label: "אירועים בחודש הקרוב",
      borderColor: "#ab47bc",
    },
  ];

  // Handler for copy link button
  const handleCopyPortalLink = (e) => {
    navigator.clipboard.writeText("https://www.simplycoach.co/athletes");
    setCopyButtonState({
      isCopied: true,
      icon: <FaCheck />,
    });
    setShowConfetti(true);
    setTimeout(() => {
      setCopyButtonState({
        isCopied: false,
        icon: <FaCopy />,
      });
    }, 2000);
    setTimeout(() => {
      setShowConfetti(false);
    }, 1800);
  };

  // Help modal footer buttons
  const helpFooterButtons = [
    {
      title: "קח אותי לסיור",
      style: "primary",
      onClick: () => {
        setShowHelpModal(false);
        setTimeout(() => setRunTour(true), 300); // Delay to allow modal to close
      },
    },
    {
      title: "הבנתי",
      style: "grey",
      onClick: () => setShowHelpModal(false),
    },
  ];

  // Helper to get student name by id
  function getStudentNameById(id) {
    if (!id) return "ספורטאי";
    const student = students.find((s) => s.id === id);
    return student ? student.studentName || student.name || id : id;
  }

  // Helper to get event name by id
  function getEventNameById(id) {
    if (!id) return "אירוע";
    const event = events.find((e) => e.id === id);
    return event ? event.eventName || event.name || id : id;
  }

  // Helper to render action text
  function renderLogActionText(log) {
    switch (log.action) {
      case "athlete_entered_portal":
        return "נכנס לפורטל";
      case "athlete_submit_new_eval_report": {
        const eventName =
          log.context && log.context.eventId
            ? getEventNameById(log.context.eventId)
            : null;
        return eventName ? `הזין משוב לאירוע ${eventName}` : "הזין משוב";
      }
      case "athlete_update_existing_eval_report": {
        const eventName =
          log.context && log.context.eventId
            ? getEventNameById(log.context.eventId)
            : null;
        return eventName ? `עדכן משוב לאירוע ${eventName}` : "עדכן משוב";
      }
      case "athlete_update_personal_details":
        return "עדכן פרטים אישיים";
      default:
        return log.action;
    }
  }
  // Helper to format log time
  function formatLogTime(timestamp) {
    if (!timestamp) return "";
    let dateObj;
    if (timestamp.seconds) {
      dateObj = new Date(timestamp.seconds * 1000);
    } else if (timestamp.toDate) {
      dateObj = timestamp.toDate();
    } else {
      dateObj = new Date(timestamp);
    }
    const now = new Date();
    const diffMs = now - dateObj;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "הרגע";
    if (diffMins < 60) return `לפני ${diffMins} דקות`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `לפני ${diffHours} שעות`;
    const diffDays = Math.floor(diffHours / 24);
    return `לפני ${diffDays} ימים`;
  }

  return (
    <div
      className="community-hub-pane"
      style={{
        padding: "32px 0",
        minHeight: 600,
        background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
        position: "relative",
      }}
    >
      {/* Help (tour) button - top left, round icon */}
      <button
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          backgroundColor: "#1976d2",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.3s ease",
          zIndex: 100,
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
        onClick={() => setShowHelpModal(true)}
        title="קהילת הארגון"
        className="tour-guide-button"
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
        }}
      >
        <FaQuestionCircle size={20} />
      </button>
      {/* Responsive grid styles */}
      <style>{responsiveGridStyles}</style>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <h1
          style={{
            fontWeight: 800,
            fontSize: "2.5rem",
            letterSpacing: 1,
            marginBottom: 0,
          }}
        >
          קהילת הארגון
        </h1>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 8,
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#4caf50",
              display: "inline-block",
              boxShadow: "0 0 6px #4caf50",
            }}
          ></span>
          <span
            style={{ fontSize: "1.1rem", color: "#4caf50", fontWeight: 600 }}
          >
            פורטל הספורטאים תקין
          </span>
        </div>
        {/* Copy Portal Link Button */}
        <div
          style={{
            marginTop: 16,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            onClick={handleCopyPortalLink}
            className="copy-portal-link-btn"
            style={{
              background: copyButtonState.isCopied ? "#43a047" : "#6c757d",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: "1.1rem",
              padding: "10px 32px",
              boxShadow: copyButtonState.isCopied
                ? "0 0 0 4px #43a04744"
                : "0 2px 8px rgba(108, 117, 125, 0.3)",
              transition: "all 0.3s ease",
              cursor: copyButtonState.isCopied ? "not-allowed" : "pointer",
              outline: copyButtonState.isCopied
                ? "2px solid #43a047"
                : undefined,
              animation: copyButtonState.isCopied ? "pulse 0.5s" : undefined,
              minWidth: 200,
              marginBottom: "15px",
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              direction: "rtl",
            }}
            disabled={copyButtonState.isCopied}
            onMouseEnter={(e) => {
              if (!copyButtonState.isCopied) {
                e.currentTarget.style.background = "#5a6268";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(108, 117, 125, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!copyButtonState.isCopied) {
                e.currentTarget.style.background = "#6c757d";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(108, 117, 125, 0.3)";
              }
            }}
          >
            <span>{copyButtonState.icon}</span>
            {copyButtonState.isCopied ? "הועתק!" : "העתק קישור לפורטל"}
          </button>
        </div>
      </div>

      {/* Stats Grid - pure CSS grid */}
      <div className="custom-stats-grid" style={gridStyles} dir="rtl">
        {stats.map((stat, idx) => (
          <Card
            key={idx}
            className="stat-card text-center shadow-sm"
            style={statCardStyle(stat.borderColor)}
            onMouseOver={(e) =>
              (e.currentTarget.style.boxShadow = `0 4px 24px ${stat.borderColor}33`)
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)")
            }
          >
            <Card.Body
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 28,
              }}
            >
              {stat.icon}
              <div style={statNumberStyle}>{stat.value}</div>
              <div style={statLabelStyle}>{stat.label}</div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Feedback Templates Button - Centered and Styled */}
      <style>{`
                .centered-feedback-btn {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin: 0 auto 24px auto;
                }
                .styled-feedback-btn {
                    background: #1976d2;
                    color: #fff !important;
                    border: none;
                    border-radius: 999px;
                    font-weight: 700;
                    font-size: 1.15rem;
                    padding: 12px 36px;
                    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.10);
                    transition: background 0.2s, transform 0.1s, box-shadow 0.2s;
                }
                .styled-feedback-btn:hover, .styled-feedback-btn:focus {
                    background: #1251a3;
                    color: #fff !important;
                    transform: translateY(-2px) scale(1.04);
                    box-shadow: 0 4px 16px rgba(25, 118, 210, 0.18);
                }
                .styled-feedback-btn:active {
                    background: #0d3570;
                    color: #fff !important;
                    transform: scale(0.98);
                    box-shadow: 0 1px 4px rgba(25, 118, 210, 0.10);
                }
            `}</style>
      <div className="centered-feedback-btn">
        <Button
          className="styled-feedback-btn d-flex align-items-center gap-2 justify-content-center"
          onClick={() => setShowFeedbackTemplatesModal(true)}
        >
          <FaChartBar /> תבניות משוב
        </Button>
      </div>

      {/* Feedback Templates Modal */}
      <FeedbackTemplatesModal
        isOpen={showFeedbackTemplatesModal}
        onClose={() => setShowFeedbackTemplatesModal(false)}
        evaluationTemplates={evaluationTemplates}
        setEvaluationTemplates={setEvaluationTemplates}
        userID={currentUser?.uid}
        activateAlert={activateAlert}
      />

      {/* Activity Feed & Settings Row - Equal Height Flexbox */}
      <Container>
        <div
          className="custom-equal-cards-row"
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 24,
            maxWidth: 1200,
            margin: "32px auto 0 auto",
            alignItems: "stretch",
          }}
        >
          <div style={{ flex: 7, minWidth: 0, display: "flex" }}>
            <Card
              className="activity-feed-card shadow-sm"
              style={{
                borderRadius: 16,
                minHeight: 260,
                height: "100%",
                width: "100%",
              }}
            >
              <Card.Header
                style={{
                  background: "#fff",
                  borderRadius: "16px 16px 0 0",
                  fontWeight: 700,
                  fontSize: "1.15rem",
                }}
              >
                פעילות אחרונה בפורטל
              </Card.Header>
              <Card.Body style={{ maxHeight: 220, overflowY: "auto" }}>
                {activityLoading ? (
                  <div style={{ textAlign: "center", color: "#888" }}>
                    טוען פעילות...
                  </div>
                ) : activityError ? (
                  <div style={{ textAlign: "center", color: "#d32f2f" }}>
                    {activityError}
                  </div>
                ) : activityLogs.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#888" }}>
                    אין פעילות להצגה
                  </div>
                ) : (
                  activityLogs.map((log, idx) => (
                    <div
                      key={log.id || idx}
                      className="d-flex align-items-center mb-3 gap-2 flex-row-reverse"
                    >
                      <FaCheckCircle
                        style={{ color: "#43a047", fontSize: 20 }}
                      />
                      <span style={{ fontWeight: 600 }}>
                        {getStudentNameById(log.actorId)}
                      </span>
                      <span style={{ color: "#888" }}>
                        {renderLogActionText(log)}
                      </span>
                      <span
                        className="ms-auto"
                        style={{ color: "#bbb", fontSize: "0.95em" }}
                      >
                        {formatLogTime(log.timestamp)}
                      </span>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>

      {/* Help Modal */}
      <CustomModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={"הסבר על פורטל הספורטאים"}
        width={50}
        height={80}
        footerButtons={helpFooterButtons}
      >
        <div style={{ direction: "rtl", fontSize: "1.1rem", lineHeight: 1.7 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: "1.3rem",
              marginBottom: 16,
              marginTop: 16,
              textAlign: "right",
            }}
          >
            מה זה פורטל הספורטאים?
          </div>
          <div style={{ textAlign: "right" }}>
            מעכשו הספורטאים שלך יוכלו להתחבר לאתר אינטרנט פשוט דרכו יוכלו לקבל
            ולשלוח מידע למערכת האישית שלך ב-SimplyCoach. האתר הזה נקרא{" "}
            <b>פורטל הספורטאים!</b>
          </div>
          <div style={{ textAlign: "right" }}>
            דרך הפורטל, הספורטאים יוכלו לצפות בזמני האימון של הקבוצה שלהם, מתי
            האירועים הבאים שלהם, לעדכן פרטים אישיים ולהזין משובים לאירועים.
          </div>
          <div
            style={{
              fontWeight: 800,
              fontSize: "1.3rem",
              marginBottom: 16,
              marginTop: 16,
              textAlign: "right",
            }}
          >
            משובים לאירועים - לאסוף מידע בקלות בצורה שמתאימה לך אישית
          </div>
          <div style={{ textAlign: "right" }}>
            תדמיינו שאחרי כל אירוע בארגון שלכם, הייתם יכולים לאסוף כל מידע שתרצו
            מהספורטאים והמאמנים שלכם!
            <br />
            לדוגמא:
            <ul>
              <li>תוצאות של כל ספורטאי מתחרות</li>
              <li>משוב מאמן למבחנים שבוצעו</li>
              <li>סקר שביעות רצון אחרי מחנה אימונים</li>
            </ul>
            <b>כל זה אפשרי עם משובים!</b>
            <br />
            <br />
            התהליך ממש פשוט:
            <ol>
              <li>
                <b>יצירת תבנית משוב</b> - במסך הקהילה תוכלו להגדיר תבניות משוב,
                שהן בעצם סט של שאלות שאותן תרצו שהספורטאים והמאמנים ימלאו. הכל
                בהתאמה אישית מושלמת.
              </li>
              <li>
                <b>שיוך תבנית משוב לאירוע</b> - ברגע יצירת אירוע, תוכלו לבחור
                איזה תבנית משוב לשייך לאירוע, ובעצם להגדיר איזה מידע תרצו לאסוף
                לאירוע הזה.
              </li>
              <li>
                <b>המאמנים מזינים משוב לאירוע</b> - אתם והמאמנים שלכם תוכלו
                להיכנס למסך האירועים באפליקציית המובייל, להיכנס למסך האירוע
                ולמלא משוב לפי תבנית המשוב שהגדרתם.
              </li>
              <li>
                <b>הספורטאים מזינים משוב לאירוע</b> - בתום האירוע, הספורטאים
                יוכלו להיכנס לפורטל הספורטאים ויוכלו למלא משוב לפי התבנית משוב
                שהגדרתם.
              </li>
            </ol>
            <b>זהו!</b>
            <br />
            יש מספר דרכים לצפות ולנתח את המידע שנאסף:
            <ul>
              <li>
                ספורטאים יוכלו לצפות במשובי מאמן שנכתבו עליהם דרך פורטל
                הספורטאים, וגם יוכלו לצפות במשובים שהזינו בעצמם.
              </li>
              <li>
                מאמנים יוכלו להיכנס למסך האירוע באפליקציית המובייל ולקרוא משובים
                שנכתבו על ידי כל ספורטאי.
              </li>
              <li>
                במסך הדוחות באפליקציית ה-WEB ניתן להפיק דוח נתוני ספורטאי שמסכם
                את כלל המשובים שהוזנו על ידי הספורטאי ועבור הספורטאי.
              </li>
              <li>אנחנו עובדים על דרכים נוספות!</li>
            </ul>
            <br />
          </div>
        </div>
      </CustomModal>
      {/* Joyride Tour */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#1976d2",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#1976d2",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
    </div>
  );
};

export default CommunityPane;
